import { useWebAuth } from '../contexts/WebAuthContext';

/**
 * AdminGuard プロパティ
 */
export interface AdminGuardProps {
  /** 子要素 */
  children: React.ReactNode;
}

/**
 * 管理者専用ガードコンポーネント
 *
 * adminグループに所属していないユーザーにはアクセス拒否画面を表示する
 */
export function AdminGuard({ children }: AdminGuardProps) {
  const { isAuthenticated, isLoading, user } = useWebAuth();

  // ローディング中
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-muted border-l-primary-500 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-muted-foreground">認証を確認中...</p>
        </div>
      </div>
    );
  }

  // 未認証の場合
  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-foreground mb-2">ログインが必要です</h1>
          <p className="text-muted-foreground mb-6">
            管理画面にアクセスするにはログインが必要です。
          </p>
          <a
            href="/login?redirect=/admin"
            className="inline-block px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            ログイン
          </a>
        </div>
      </div>
    );
  }

  // adminグループに所属しているか確認
  const isAdmin = user.groups?.includes('admin');

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">⛔</div>
          <h1 className="text-2xl font-bold text-foreground mb-2">アクセス権限がありません</h1>
          <p className="text-muted-foreground mb-6">
            このページは管理者専用です。
            <br />
            管理者権限が必要な場合はシステム管理者にお問い合わせください。
          </p>
          <a
            href="/download"
            className="inline-block px-6 py-3 bg-accent text-foreground rounded-lg hover:bg-accent/80 transition-colors"
          >
            ダウンロードページへ
          </a>
        </div>
      </div>
    );
  }

  // 認証済み + adminグループ所属、子要素を表示
  return <>{children}</>;
}
